import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuestLoginPayload, LoginCredentials, RegisterPayload } from '@matchroom/shared';
import { authService } from '@/services/auth.service';
import { disconnectRoomSocket } from '@/services/socket.service';
import { useAuthStore } from '@/stores/auth.store';
import { useParticipantStore } from '@/stores/participant.store';
import { useRoomStore } from '@/stores/room.store';
import { queryKeys } from '@/lib/query-client';
import { ApiClientError } from '@/lib/api-client';

export function useRegisterMutation() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RegisterPayload) => authService.register(payload),
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, { user: data.user });
    },
  });
}

export function useLoginMutation() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LoginCredentials) => authService.login(payload),
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, { user: data.user });
    },
  });
}

export function useGuestLoginMutation() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: GuestLoginPayload) => {
      clearAuth();
      disconnectRoomSocket();
      useParticipantStore.getState().clear();
      useRoomStore.getState().clear();
      queryClient.clear();
      return authService.guestLogin(payload);
    },
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, { user: data.user });
    },
  });
}

export function useLogoutMutation() {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      clearAuth();
      queryClient.clear();
    },
  });
}

export function useRefreshMutation() {
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  return useMutation({
    mutationFn: () => authService.refresh(),
    onSuccess: (data) => {
      setAccessToken(data.tokens.accessToken);
    },
  });
}

export function useMeQuery(enabled = true) {
  const setUser = useAuthStore((s) => s.setUser);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  return useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: async () => {
      try {
        const data = await authService.me();
        setUser(data.user);
        return data;
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) {
          clearAuth();
        }
        throw error;
      }
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function getAuthErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.code === 'NETWORK_ERROR') {
      const onProd =
        typeof window !== 'undefined' &&
        !window.location.hostname.includes('localhost') &&
        !window.location.hostname.startsWith('192.168.');
      return onProd
        ? "Can't reach the MatchRoom API. The website is up but the backend may be offline — try again shortly, or ask the host to run update.sh on the server."
        : "Can't reach the server right now. Check your connection and try again.";
    }
    switch (error.code) {
      case 'AUTH_INVALID_CREDENTIALS':
        return 'Invalid email or password';
      case 'AUTH_EMAIL_TAKEN':
        return 'Email already exists';
      case 'AUTH_USERNAME_TAKEN':
        return 'Username already taken';
      case 'AUTH_GUEST_NAME_TAKEN':
        return 'This name belongs to a registered account — choose another or sign in';
      case 'RATE_LIMITED':
        return 'Too many requests — wait a moment and try again';
      case 'VALIDATION_ERROR':
        return error.message;
      default:
        return error.message;
    }
  }
  return 'Something went wrong. Please try again.';
}
